import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCreateChannel, useChannelDetail } from "../../hooks/useChannels";
import { useAgents } from "../../hooks/useAgents";
import { useChannelSocket } from "../../hooks/useChannelSocket";
import channelService from "../../services/channelService";
import api from "../../services/api";
import toast from "react-hot-toast";
import {
  CheckCircle,
  Check,
  QrCode,
  Wifi,
  WifiOff,
  ChevronRight,
  Bot,
  User,
  Globe,
  Settings,
  Clock,
  Smartphone,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";

const CreateChannelPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current step and channelId from URL params
  const currentStep = parseInt(searchParams.get("step")) || 1;
  const channelId = searchParams.get("channelId");

  const { formData, updateFormData, resetForm, createChannel } =
    useCreateChannel();

  // Get agents for dropdown selection
  const {
    data: agentsData,
    isLoading: isLoadingAgents,
    error: agentsError,
  } = useAgents();
  const agents = agentsData?.agents || [];
  const activeAgents = agents.filter((agent) => agent.status === "active");

  // Channel detail for Step 3 (when channelId is available)
  const { channel: channelDetail, isLoading: isLoadingDetail } =
    useChannelDetail(channelId);

  // Local state for real-time features (channelId now comes from URL)
  const [isCreating, setIsCreating] = useState(false);
  const [creationError, setCreationError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingStage, setLoadingStage] = useState(0);
  const [retryInfo, setRetryInfo] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Real-time socket handlers
  const handleQRCode = useCallback((qrData) => {
    console.log("📱 QR Code received:", qrData);
    toast.success("QR Code updated! Please scan with your device.");
  }, []);

  const handleWhatsAppConnected = useCallback(
    (connectionData) => {
      console.log("🟢 WhatsApp Connected:", connectionData);
      toast.success(`WhatsApp connected: ${connectionData.accountName}`);

      // Auto proceed to next step after connection, keeping channelId
      setTimeout(() => {
        setSearchParams({ step: "3", channelId: channelId || "" });
      }, 2000);
    },
    [setSearchParams, channelId]
  );

  const handleWhatsAppDisconnected = useCallback((disconnectionData) => {
    console.log("🔴 WhatsApp Disconnected:", disconnectionData);
    toast.error(`WhatsApp disconnected: ${disconnectionData.reason}`);
  }, []);

  const handleChannelCreated = useCallback(
    (channelData) => {
      console.log("📋 Channel Created:", channelData);
      const newChannelId = channelData.channel?.id || channelData.channelId;

      // Update URL with channelId
      if (newChannelId) {
        setSearchParams({ step: "2", channelId: newChannelId.toString() });
      }

      // Extract QR code from authSetup if available
      if (channelData.authSetup?.qrCode) {
        console.log("🎯 QR Code available in authSetup");
        // QR code will be handled by socket hook
      }

      toast.success("Channel created successfully!");
    },
    [setSearchParams]
  );

  // Socket integration
  const {
    isConnected,
    connectionStatus,
    connectedInfo,
    qrCode,
    qrExpiry,
    getQRTimeRemaining,
    isQRExpired,
  } = useChannelSocket({
    channelId,
    onQRCode: handleQRCode,
    onWhatsAppConnected: handleWhatsAppConnected,
    onWhatsAppDisconnected: handleWhatsAppDisconnected,
    onChannelCreated: handleChannelCreated,
  });

  // Manual QR extraction if socket doesn't provide it
  useEffect(() => {
    if (channelId && !qrCode) {
      console.log("🔍 No QR code from socket, attempting manual fetch...");

      // Set timeout for QR generation (90 seconds)
      const qrTimeout = setTimeout(() => {
        if (!qrCode) {
          console.warn("⚠️ QR code generation timeout");
          toast.error(
            "QR code generation is taking longer than expected. You can try refreshing or check the channels list.",
            {
              duration: 8000,
            }
          );
        }
      }, 90000);

      return () => clearTimeout(qrTimeout);
    }
  }, [channelId, qrCode]);

  // QR retry function
  const handleRetryQR = async () => {
    if (!channelId) return;

    try {
      toast.loading("Refreshing QR code...");
      const response = await channelService.getChannelQR(channelId, true);
      if (response.success && response.data.qrCode) {
        toast.success("QR code refreshed!");
      }
    } catch (error) {
      toast.error("Failed to refresh QR code. Please try again.");
    }
  };

  // WhatsApp setup retry function
  const handleRetryWhatsAppSetup = async () => {
    if (!retryInfo?.endpoint) return;

    setIsRetrying(true);

    try {
      toast.loading("Retrying WhatsApp setup...", { id: "retry-setup" });

      const response = await api.post(retryInfo.endpoint);

      if (response.data) {
        const authSetup = response.data.authSetup || response.data;

        if (authSetup?.status === "ready" || authSetup?.qrCode) {
          toast.success("WhatsApp setup successful!", { id: "retry-setup" });
          setRetryInfo(null);

          // QR should come via socket or direct response
          if (authSetup.qrCode) {
            console.log("🎯 QR code from retry:", authSetup.qrCode);
          }
        } else if (authSetup?.status === "failed") {
          throw new Error(authSetup.error || "Retry failed");
        } else {
          toast.success("Setup retried! Waiting for QR code...", {
            id: "retry-setup",
          });
          setRetryInfo(null);
        }
      } else {
        throw new Error("Invalid retry response");
      }
    } catch (error) {
      console.error("Retry error:", error);
      toast.error(`Retry failed: ${error.message}`, { id: "retry-setup" });
    } finally {
      setIsRetrying(false);
    }
  };

  // Form handlers
  const handleNameChange = (e) => {
    updateFormData({ name: e.target.value });
  };

  const handleTypeChange = (e) => {
    updateFormData({ type: e.target.value });
  };

  const handleAgentIdChange = (e) => {
    updateFormData({ agentId: e.target.value });
  };

  const handleBusinessNameChange = (e) => {
    updateFormData({
      config: {
        ...formData.config,
        business_name: e.target.value,
      },
    });
  };

  // Step navigation with real API integration
  const handleNext = async () => {
    if (currentStep === 1) {
      // Validate form
      if (
        !formData.name ||
        !formData.agentId ||
        !formData.config?.business_name
      ) {
        if (!formData.name) {
          toast.error("Please enter a channel name");
        } else if (!formData.agentId) {
          toast.error("Please select an AI agent");
        } else if (!formData.config?.business_name) {
          toast.error("Please enter a business name");
        }
        return;
      }

      // Validate agent exists
      const selectedAgent = activeAgents.find(
        (a) => a.id.toString() === formData.agentId.toString()
      );
      if (!selectedAgent) {
        toast.error(
          "Selected agent is not available. Please choose another agent."
        );
        return;
      }

      // Create channel via API
      setIsCreating(true);
      setCreationError(null);
      setLoadingStage(1);
      setLoadingMessage("Creating channel...");

      // Progressive loading messages
      const loadingTimer1 = setTimeout(() => {
        setLoadingStage(2);
        setLoadingMessage("Setting up WhatsApp service...");
      }, 3000);

      const loadingTimer2 = setTimeout(() => {
        setLoadingStage(3);
        setLoadingMessage("Generating QR code...");
      }, 20000);

      const loadingTimer3 = setTimeout(() => {
        setLoadingStage(4);
        setLoadingMessage("Almost ready, please wait...");
      }, 60000);

      const loadingTimer4 = setTimeout(() => {
        setLoadingStage(4);
        setLoadingMessage("Taking longer than usual, but still processing...");
      }, 90000);

      try {
        const response = await createChannel();

        // Clear all timers
        clearTimeout(loadingTimer1);
        clearTimeout(loadingTimer2);
        clearTimeout(loadingTimer3);
        clearTimeout(loadingTimer4);

        if (response.success && response.data) {
          // Channel always created successfully if we reach here
          const newChannelId = response.data.channel?.id;
          const authSetup = response.data.authSetup;

          console.log("✅ Channel created:", response.data);

          // ALWAYS navigate to Step 2 with channelId in URL
          setSearchParams({ step: "2", channelId: newChannelId.toString() });

          // Handle authSetup status
          if (authSetup?.status === "ready" && authSetup.qrCode) {
            // Perfect scenario: QR ready immediately
            toast.success("Channel created with QR code ready!");
            console.log("🎯 QR code ready immediately:", authSetup.qrCode);
          } else if (authSetup?.status === "failed") {
            // WhatsApp setup failed, but channel exists
            toast.warning(
              "Channel created! WhatsApp setup failed - you can retry below."
            );
            setRetryInfo({
              endpoint: authSetup.retryEndpoint,
              error: authSetup.error || "WhatsApp setup failed",
              showRetry: true,
            });
            console.log("⚠️ AuthSetup failed:", authSetup.error);
          } else {
            // Pending or unknown status, wait for socket
            toast.success("Channel created! Setting up WhatsApp connection...");
            console.log("⏳ AuthSetup pending, waiting for socket events");
          }
        } else {
          throw new Error(response.error || "Failed to create channel");
        }
      } catch (error) {
        // Clear all timers
        clearTimeout(loadingTimer1);
        clearTimeout(loadingTimer2);
        clearTimeout(loadingTimer3);
        clearTimeout(loadingTimer4);

        console.error("Channel creation error:", error);

        // Better error handling - distinguish timeout vs real error
        let errorMessage = error.message;
        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          errorMessage =
            "Request timeout. The channel may have been created successfully. Please check the channels list or refresh this page.";
          toast.error(errorMessage, {
            duration: 8000,
            id: "timeout-error",
          });

          // Suggest navigation to channels list after timeout
          setTimeout(() => {
            toast.custom(
              (t) => (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-blue-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">
                        Check Channels List
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Your channel might have been created
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        toast.dismiss(t.id);
                        navigate("/admin/channels");
                      }}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                    >
                      View Channels
                    </button>
                  </div>
                </div>
              ),
              { duration: 10000 }
            );
          }, 2000);
        } else {
          toast.error(errorMessage);
        }

        setCreationError(errorMessage);
      } finally {
        setIsCreating(false);
        setLoadingMessage("");
        setLoadingStage(0);
      }
    } else {
      // Navigate to next step, preserving channelId
      const newStep = Math.min(currentStep + 1, 3);
      const params = { step: newStep.toString() };
      if (channelId) {
        params.channelId = channelId;
      }
      setSearchParams(params);
    }
  };

  const handleComplete = () => {
    // Reset form and navigate
    resetForm();
    toast.success("Channel setup completed successfully!");
    setTimeout(() => {
      navigate("/admin/channels");
    }, 1000);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-12">
      <div className="flex items-center space-x-4">
        {[
          { number: 1, title: "Channel Details", icon: Settings },
          { number: 2, title: "Device Connection", icon: QrCode },
          { number: 3, title: "Setup Complete", icon: CheckCircle },
        ].map((step, index) => (
          <div key={step.number} className="flex items-center">
            {/* Step Circle */}
            <div className="flex flex-col items-center">
              <div
                className={`relative w-12 h-12 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                  currentStep >= step.number
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                }`}
              >
                {currentStep > step.number ? (
                  <Check className="w-6 h-6" />
                ) : (
                  <step.icon className="w-6 h-6" />
                )}
              </div>

              {/* Step Title */}
              <div className="mt-3 text-center">
                <div
                  className={`text-sm font-medium ${
                    currentStep >= step.number
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {step.title}
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Step {step.number}
                </div>
              </div>
            </div>

            {/* Connector Line */}
            {index < 2 && (
              <div
                className={`w-16 h-1 mx-4 transition-all duration-300 ${
                  currentStep > step.number
                    ? "bg-blue-600"
                    : "bg-gray-200 dark:bg-gray-700"
                }`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Channel Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Set up your new communication channel with AI agent
        </p>
      </div>

      <div className="space-y-6">
        {/* Channel Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Channel Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="e.g., Customer Support WhatsApp"
            autoFocus
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
          />
        </div>

        {/* Channel Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Channel Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.type}
            onChange={handleTypeChange}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
          >
            <option value="">Select channel type</option>
            <option value="whatsapp">WhatsApp Business</option>
            <option value="telegram">Telegram</option>
            <option value="webchat">Web Chat</option>
            <option value="instagram">Instagram</option>
          </select>
        </div>

        {/* Agent Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            AI Agent <span className="text-red-500">*</span>
          </label>
          {isLoadingAgents ? (
            <div className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-gray-500 dark:text-gray-400">
                  Loading agents...
                </span>
              </div>
            </div>
          ) : agentsError ? (
            <div className="w-full px-4 py-3 border border-red-300 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <span className="text-red-600 dark:text-red-400">
                Failed to load agents. Please refresh the page.
              </span>
            </div>
          ) : activeAgents.length === 0 ? (
            <div className="w-full px-4 py-3 border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <span className="text-yellow-700 dark:text-yellow-400">
                No active agents available. Please create an agent first.
              </span>
            </div>
          ) : (
            <select
              value={formData.agentId}
              onChange={handleAgentIdChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
            >
              <option value="">Select an AI Agent</option>
              {activeAgents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.model_provider}/{agent.model_name})
                </option>
              ))}
            </select>
          )}
          {formData.agentId && activeAgents.length > 0 && (
            <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              {(() => {
                const selectedAgent = activeAgents.find(
                  (a) => a.id.toString() === formData.agentId.toString()
                );
                return selectedAgent ? (
                  <div className="text-sm">
                    <div className="font-medium text-blue-800 dark:text-blue-200">
                      {selectedAgent.name}
                    </div>
                    <div className="text-blue-600 dark:text-blue-300">
                      {selectedAgent.model_provider} /{" "}
                      {selectedAgent.model_name}
                    </div>
                    <div className="text-blue-500 dark:text-blue-400 mt-1">
                      {selectedAgent.description || "No description available"}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>

        {/* Business Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Business Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.config?.business_name || ""}
            onChange={handleBusinessNameChange}
            placeholder="e.g., ABC Company Customer Service"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
          />
        </div>

      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Connect Your Device
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Scan the QR code with your{" "}
          {formData.type === "whatsapp" ? "WhatsApp Business" : formData.type}{" "}
          app
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        <div className="space-y-6">
          {/* QR Code Container */}
          <div className="flex justify-center">
            <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              {qrCode ? (
                <img
                  src={
                    qrCode.startsWith("data:image")
                      ? qrCode
                      : `data:image/png;base64,${qrCode}`
                  }
                  alt="WhatsApp QR Code"
                  className="w-48 h-48 object-contain"
                />
              ) : (
                <div className="w-48 h-48 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-2 animate-spin" />
                    <p className="text-sm text-gray-500 mb-1">
                      Generating QR code...
                    </p>
                    <p className="text-xs text-gray-400">
                      This may take 30-60 seconds
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Connection Status */}
            {isConnected ? (
              <div className="flex items-center justify-center space-x-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">
                  Connected to {connectedInfo?.accountName || "WhatsApp"}!
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2 text-blue-600">
                <Wifi className="w-5 h-5" />
                <span className="font-medium">Ready to connect!</span>
              </div>
            )}

            {/* Retry Section for Failed WhatsApp Setup */}
            {retryInfo?.showRetry && !qrCode && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                      WhatsApp Setup Failed
                    </h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      {retryInfo.error}
                    </p>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                      Your channel was created successfully, but WhatsApp
                      connection failed. You can retry the setup below.
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={handleRetryWhatsAppSetup}
                    disabled={isRetrying}
                    className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white rounded-lg text-sm transition-colors"
                  >
                    {isRetrying ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Retrying...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        <span>Retry WhatsApp Setup</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => navigate("/admin/channels")}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                  >
                    View Channels
                  </button>
                </div>
              </div>
            )}

            {/* QR Code Status */}
            {qrCode && !isQRExpired() ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-green-700 dark:text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">
                    QR Code generated successfully!
                  </span>
                </div>
                <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                  QR code expires in {Math.ceil(getQRTimeRemaining() / 1000)}{" "}
                  seconds
                </p>
              </div>
            ) : qrCode && isQRExpired() ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-red-700 dark:text-red-400">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">QR Code expired</span>
                </div>
                <p className="text-sm text-red-600 dark:text-red-500 mt-1 mb-3">
                  Please refresh to generate a new QR code
                </p>
                <button
                  onClick={handleRetryQR}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh QR Code</span>
                </button>
              </div>
            ) : channelId && !qrCode ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-yellow-700 dark:text-yellow-400">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">
                    QR Code generation in progress...
                  </span>
                </div>
                <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-1 mb-3">
                  This is taking longer than usual. Please wait or try
                  refreshing.
                </p>
                <button
                  onClick={handleRetryQR}
                  className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Retry QR Generation</span>
                </button>
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-700 dark:text-blue-400">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">Generating QR Code...</span>
                </div>
                <p className="text-sm text-blue-600 dark:text-blue-500 mt-1">
                  Please wait while we set up your connection
                </p>
              </div>
            )}
          </div>

          {/* Enhanced Instructions */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              Connection Steps:
            </h3>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2 text-left">
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                  1
                </span>
                <span>
                  Open{" "}
                  {formData.type === "whatsapp"
                    ? "WhatsApp Business"
                    : formData.type || "your messaging app"}{" "}
                  on your phone
                </span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                  2
                </span>
                <span>Go to Settings → Linked Devices</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                  3
                </span>
                <span>Tap "Link a Device" and scan this QR code</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="max-w-2xl mx-auto text-center">
      {/* Success Header */}
      <div className="mb-8">
        <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-6 animate-pulse">
          <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          Channel Created Successfully!
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Your {formData.type || "messaging"} channel "
          {formData.name || "Untitled Channel"}" is now active and ready to
          handle conversations.
        </p>
      </div>

      {/* Channel Summary Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center justify-center">
          <Settings className="w-5 h-5 mr-2" />
          Channel Summary
        </h3>
        <div className="space-y-4 text-left">
          {isLoadingDetail ? (
            <div className="text-center py-4">
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Loading channel details...
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  Channel Name:
                </span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {channelDetail?.name || formData.name || "Untitled Channel"}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  Channel ID:
                </span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  #{channelId || "N/A"}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  Channel Type:
                </span>
                <span className="font-semibold text-gray-900 dark:text-white capitalize flex items-center">
                  {(channelDetail?.type || formData.type) === "whatsapp" && (
                    <span className="text-green-600 mr-2">📱</span>
                  )}
                  {channelDetail?.type || formData.type || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  Status:
                </span>
                <span
                  className={`font-semibold capitalize flex items-center ${
                    channelDetail?.status === "active"
                      ? "text-green-600 dark:text-green-400"
                      : "text-yellow-600 dark:text-yellow-400"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full mr-2 ${
                      channelDetail?.status === "active"
                        ? "bg-green-600"
                        : "bg-yellow-600"
                    }`}
                  ></div>
                  {channelDetail?.status || "Unknown"}
                </span>
              </div>
              <div className="flex justify-between items-start py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  AI Agent:
                </span>
                <div className="text-right">
                  {(() => {
                    const agentId = channelDetail?.agent_id || formData.agentId;
                    const selectedAgent = activeAgents.find(
                      (a) => a.id.toString() === agentId?.toString()
                    );

                    if (selectedAgent) {
                      return (
                        <div className="space-y-1">
                          <div className="font-semibold text-gray-900 dark:text-white flex items-center">
                            <Bot className="w-4 h-4 mr-2 text-blue-600" />
                            {selectedAgent.name}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {selectedAgent.model_provider} /{" "}
                            {selectedAgent.model_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-500">
                            ID: {selectedAgent.id}
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <span className="font-semibold text-gray-900 dark:text-white flex items-center">
                          <Bot className="w-4 h-4 mr-2 text-gray-400" />
                          {agentId ? `Agent #${agentId}` : "Not specified"}
                        </span>
                      );
                    }
                  })()}
                </div>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                  Business Name:
                </span>
                <span className="font-semibold text-gray-900 dark:text-white capitalize">
                  {channelDetail?.config?.business_name ||
                    formData.config?.business_name ||
                    "Not specified"}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Connection Status */}
      {channelDetail && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Wifi className="w-5 h-5 mr-2" />
            Connection Status
          </h3>
          <div className="text-center">
            {isConnected ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center text-green-600 dark:text-green-400">
                  <CheckCircle className="w-8 h-8 mr-2" />
                  <span className="font-semibold text-lg">Connected</span>
                </div>
                {connectedInfo?.accountName && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Account: {connectedInfo.accountName}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-center text-gray-500 dark:text-gray-400">
                  <WifiOff className="w-8 h-8 mr-2" />
                  <span className="font-semibold text-lg">Not Connected</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  WhatsApp setup may still be in progress
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/channels")}
          className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <span>View All Channels</span>
        </button>
        <button
          onClick={() => {
            resetForm();
            setSearchParams({ step: "1" }); // Reset to step 1, clear channelId
          }}
          className="w-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
        >
          Create Another Channel
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Create New Channel
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Set up your AI-powered channel in 3 simple steps
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
          {/* Error Display */}
          {creationError && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-red-700 dark:text-red-400">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Error creating channel</span>
              </div>
              <p className="text-sm text-red-600 dark:text-red-500 mt-1">
                {creationError}
              </p>
            </div>
          )}

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className="flex justify-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            {currentStep < 3 ? (
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={isCreating}
                  className="flex items-center space-x-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                >
                  {isCreating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>{loadingMessage || "Creating Channel..."}</span>
                    </>
                  ) : (
                    <>
                      <span>
                        {currentStep === 1
                          ? "Create & Generate QR Code"
                          : "Continue"}
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                {/* Loading Progress Indicator */}
                {isCreating && (
                  <div className="mt-4 space-y-2">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
                        style={{
                          width: `${Math.min((loadingStage / 4) * 100, 90)}%`,
                        }}
                      />
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {loadingStage === 1 && "Step 1/4: Creating channel..."}
                      {loadingStage === 2 &&
                        "Step 2/4: Setting up WhatsApp service..."}
                      {loadingStage === 3 && "Step 3/4: Generating QR code..."}
                      {loadingStage === 4 && "Step 4/4: Finalizing setup..."}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      This process may take 60-120 seconds. Please don't close
                      this page.
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleComplete}
                className="flex items-center space-x-2 px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
              >
                <span>Complete Setup</span>
                <CheckCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateChannelPage;
